import Button from "@app/components/button";
import Footer from "@app/components/footer";
import ForgotPasswordModal from "@app/components/forgot-password-modal";
import Input from "@app/components/input";
import LogoutModal from "@app/components/logout-modal";
import OtpConfirmModal from "@app/components/otp-confirm-modal";
import SignIn from "@app/components/sign-in";
import SignUp from "@app/components/sign-up";
import { addToast } from "@app/components/toast/toast.service";
import {
  ACCESS_TOKEN_KEY,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  KeyboardCode,
  REFRESH_TOKEN_KEY,
  Role,
  RoutePath,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import MenuIcon from "@app/icons/menu";
import AuthService from "@app/services/http/auth.service";
import NotificationService from "@app/services/http/notification.service";
import { openPortalDialog } from "@app/services/modal.service";
import { clearUser, selectAuth, storeUser } from "@app/store/auth";
import { fetchBannerAdsAction } from "@app/store/banner-ads";
import { fetchCategoriesAction, selectCategories } from "@app/store/category";
import {
  BookOptionOption,
  Category,
  GoogleCredentialJwtPayload,
  InfoConfirmOtp,
  LoginRequest,
  LoginResponse,
  Meta,
  Notification,
  SignUpForm,
  SignUpRequest,
  SocialLoginRequest,
  UserData,
} from "@app/types";
import { clearLocalStorage, formatDateTime } from "@app/utils/util";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import { RequestParams } from "@core/services/http/http.type";
import StorageService from "@core/services/storage";
import { CredentialResponse } from "@react-oauth/google";
import clsx from "clsx";
import { jwtDecode } from "jwt-decode";
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ReactFacebookFailureResponse,
  ReactFacebookLoginInfo,
} from "react-facebook-login";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, createSearchParams, useNavigate } from "react-router-dom";

const UserLayout = () => {
  const userMenuRef = createRef<HTMLDivElement>();
  const inputSearchRef = createRef<HTMLInputElement>();
  const signInModalRef = useRef<HTMLDivElement>(null);
  const signUpModalRef = useRef<HTMLDivElement>(null);
  const forgotPasswordModalRef = useRef<HTMLDivElement>(null);
  const otpConfirmModalRef = useRef<HTMLDivElement>(null);

  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSearchInputShow, setIsSearchInputShow] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [isOtpConfirmModalOpen, setIsOtpConfirmModalOpenModalOpen] =
    useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const categoryState = useSelector(selectCategories);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [meta, setMeta] = useState<Meta>();
  const [keyword, setKeyword] = useState("");
  const [requestParams, setRequestParams] = useState<RequestParams>({
    ...DEFAULT_REQUEST_PARAMS,
  });

  const userInfo = useSelector(selectAuth);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [update, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();

  useEffect(() => {
    dispatch(fetchCategoriesAction());
    dispatch(fetchBannerAdsAction());
  }, []);

  useEffect(() => {
    if (userInfo.id) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        pageNum: DEFAULT_PAGE,
      };

      NotificationService.getList(
        newRequestParams,
        abortController.signal
      ).then((res) => {
        setMeta(res.data.meta);
        setNotifications(res.data.items);
      });
    }
  }, [update]);

  useEffect(() => {
    if (
      requestParams?.pageNum &&
      Number(requestParams.pageNum) > DEFAULT_PAGE
    ) {
      NotificationService.getList(requestParams, abortController.signal).then(
        (res) => {
          const newNotifications: Notification[] = [
            ...notifications,
            ...res.data.items,
          ];

          setMeta(res.data.meta);
          setNotifications(newNotifications);
        }
      );
    }
  }, [requestParams]);

  useEffect(() => {
    if (isSignInModalOpen || isSignUpModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isSignInModalOpen, isSignUpModalOpen]);

  useOnClickOutside(userMenuRef, () => {
    setIsUserMenuOpen(false);
  });

  const handleOpenSignInModal = () => {
    setIsSignInModalOpen(true);
    setIsSignUpModalOpen(false);
  };

  const handleOpenSignUpModal = () => {
    setIsSignUpModalOpen(true);
    setIsSignInModalOpen(false);
  };

  const handleOpenForgotPasswordModal = () => {
    setIsSignInModalOpen(false);

    const forgotPasswordObs = openPortalDialog(ForgotPasswordModal);

    forgotPasswordObs.afterClosed().subscribe((data) => {
      if (data?.username) {
        AuthService.forgotPassword(data.username).then((res) => {
          addToast({ text: res.data.message });

          handleOpenOtpConfirmModal({ ...res.data, username: data.username });
        });
      }
    });
  };

  const handleOpenOtpConfirmModal = (infoConfirmOtp: InfoConfirmOtp) => {
    const otpConfirmObs = openPortalDialog(OtpConfirmModal, {
      infoConfirmOtp,
    });

    otpConfirmObs.afterClosed().subscribe((data) => {
      if (data?.otp) {
        AuthService.confirmChangePassword(data.otp, data.password).then(() => {
          addToast({ text: SystemMessage.CHANGE_PASSWORD_SUCCESSFULLY });
        });
      }
    });
  };

  const handleNavigateHome = () => {
    navigate(RoutePath.HOME);
  };

  const handleNavigateUpgrade = () => {
    navigate(RoutePath.UPGRADE);
  };

  const handleSearchInputShow = () => {
    setIsSearchInputShow(!isSearchInputShow);
    inputSearchRef.current?.focus();
  };

  const handleUserMenuClick = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleNotificationClick = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const handleLogoutClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsSignInModalOpen(false);

    const logoutModalObs = openPortalDialog(LogoutModal);

    logoutModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        clearLocalStorage();
        dispatch(clearUser());
        navigate("/");
      }
    });
  };

  const handleProfileClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
    navigate(`${RoutePath.USER_PROFILE}/${RoutePath.USER_ACCOUNT}`);
  };

  const onIconSearchClick = () => {
    navigate({
      pathname: RoutePath.SEARCH,
      search: createSearchParams({
        keyword,
      }).toString(),
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === KeyboardCode.ENTER || e.code === KeyboardCode.NUMPAD_ENTER) {
      onIconSearchClick();
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const handleNotificationItemClick = (notification: Notification) => {
    setIsNotificationOpen(false);
    navigate(notification.navigateTo);
    NotificationService.markAsRead(notification.id).then(() => {
      forceUpdate();
    });
  };

  const handleMarkAllReadClick = () => {
    setIsNotificationOpen(false);
    NotificationService.markAllRead().then(() => {
      forceUpdate();
    });
  };

  const handleCategoryItemClick = (category: Category) => {
    navigate(`${RoutePath.BOOK_BY_CATEGORY}?category_id=${category.id}`);
    setIsCategoryMenuOpen(false);
  };

  const onLoadMoreClick = () => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      pageNum: Number(requestParams.pageNum) + 1,
    };

    setRequestParams(newRequestParams);
  };

  const handleCategoryClick = () => {
    setIsCategoryMenuOpen(!isCategoryMenuOpen);
  };

  useOnClickOutside(signInModalRef, () => {
    if (isSignInModalOpen) {
      setIsSignInModalOpen(false);
    }
  });

  useOnClickOutside(signUpModalRef, () => {
    if (isSignUpModalOpen) {
      setIsSignUpModalOpen(false);
    }
  });

  useOnClickOutside(forgotPasswordModalRef, () => {
    if (isForgotPasswordModalOpen) {
      setIsForgotPasswordModalOpen(false);
    }
  });

  useOnClickOutside(otpConfirmModalRef, () => {
    if (isOtpConfirmModalOpen) {
      setIsOtpConfirmModalOpenModalOpen(false);
    }
  });

  const handleLogin = (res: LoginResponse) => {
    const { accessToken, refreshToken, user, categories } = res.data;

    // Commented temporary to allow author login
    /*
    if (user.role !== Role.USER) {
      addToast({ text: SystemMessage.ACCESS_DENIED, status: "inValid" });

      return;
    }
    */

    const userData: UserData = {
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role,
      fullName: user.fullName,
      phone: user.phone,
      thumbnail: user.thumbnail,
    };

    StorageService.set(ACCESS_TOKEN_KEY, accessToken);
    StorageService.set(REFRESH_TOKEN_KEY, refreshToken);
    StorageService.setObject(USER_INFO_KEY, userData);
    dispatch(
      storeUser({
        ...user,
        categories,
      })
    );

    setIsSignInModalOpen(false);
    navigate(RoutePath.HOME);
  };

  const onSignInSubmit = (values: LoginRequest) => {
    AuthService.login(values.email, values.password).then((res) => {
      handleLogin(res);
    });
  };

  const onGoogleLoginSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      const decoded = jwtDecode<GoogleCredentialJwtPayload>(
        credentialResponse.credential
      );

      const body: SocialLoginRequest = {
        email: decoded.email,
        fullName: decoded.name,
        idToken: credentialResponse.credential,
        provider: "GOOGLE",
        picture: decoded.picture ?? "",
      };

      AuthService.loginSocial(body).then((res) => {
        handleLogin(res);
      });
    }
  };

  const onFacebookLoginSuccess = (
    userInfo: ReactFacebookLoginInfo | ReactFacebookFailureResponse
  ) => {
    if ((userInfo as ReactFacebookFailureResponse)?.status === "unknown") {
      addToast({ text: SystemMessage.LOGIN_FAILED, status: "inValid" });

      return;
    }

    const data = userInfo as ReactFacebookLoginInfo;

    const body: SocialLoginRequest = {
      email: data.email ?? "",
      fullName: data.name ?? "",
      idToken: data.accessToken,
      provider: "FACEBOOK",
      picture: data.picture?.data?.url ?? "",
    };

    AuthService.loginSocial(body).then((res) => {
      handleLogin(res);
    });
  };

  const onSignUpSubmit = (values: SignUpForm) => {
    const signUpRequest: SignUpRequest = {
      fullName: values.fullName,
      email: values.email,
      username: values.username,
      password: values.password,
      phone: values.phone,
      fcmToken: values.fcmToken,
    };

    AuthService.signUp(signUpRequest).then((res) => {
      const { accessToken, refreshToken, user, categories } = res.data;

      const userData: UserData = {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
        fullName: user.fullName,
        phone: user.phone,
        thumbnail: user.thumbnail,
      };

      StorageService.set(ACCESS_TOKEN_KEY, accessToken);
      StorageService.set(REFRESH_TOKEN_KEY, refreshToken);
      StorageService.setObject(USER_INFO_KEY, userData);
      dispatch(
        storeUser({
          ...user,
          categories,
        })
      );

      setIsSignUpModalOpen(false);
      navigate(RoutePath.HOME);
    });
  };

  const handleClearClick = () => {
    setKeyword("");
    inputSearchRef.current?.focus();
  };

  const handleBookOptionClick = (option: BookOptionOption) => {
    navigate(`${RoutePath.BOOK_BY_OPTION}?option=${option}`);
  };

  return (
    <div className="bg-[#F5F2EE] min-h-screen">
      <div className="sticky top-0 bg-[#F5F2EE] z-[999999] flex h-[92px] justify-between px-[100px] max-lg:px-[60px] items-center">
        <div className="flex justify-between items-center gap-10">
          <img
            className="w-[114px] cursor-pointer"
            src={Images.LogoText.default}
            alt=""
            onClick={handleNavigateHome}
          />
          <div className="font-bold text-lg cursor-default">
            <div
              className={clsx(
                "select-none font-[SVN-Gilroy] text-[#171A20] flex items-center gap-1 font-semibold tracking-[0.027px] cursor-pointer",
                {
                  "text-[#38499b] [&_.fill]:fill-[#38499b] [&_.stroke]:stroke-[#38499b]":
                    isCategoryMenuOpen,
                }
              )}
              onClick={handleCategoryClick}
            >
              <MenuIcon />
              Thể loại
            </div>
            {isCategoryMenuOpen && (
              <div className="select-none absolute overscroll-contain max-h-96 overflow-y-auto p-2 bg-white border border-[#DFE2E7] rounded-xl shadow-[0_8px_20px_0_rgba(255,255,255,0.28)] z-10">
                {categoryState.list.map((item) => (
                  <div
                    key={item.id}
                    className="cursor-pointer text-center flex justify-center items-center hover:bg-gray-100 hover:rounded-lg font-medium py-4 px-3"
                    onClick={() => handleCategoryItemClick(item)}
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="select-none font-[SVN-Gilroy] text-[#171A20] font-semibold tracking-[0.027px] cursor-pointer"
            onClick={() => handleBookOptionClick(BookOptionOption.FREE)}
          >
            Sách Free
          </div>
          <div
            className="select-none font-[SVN-Gilroy] text-[#171A20] font-semibold tracking-[0.027px] cursor-pointer"
            onClick={() => handleBookOptionClick(BookOptionOption.VIP)}
          >
            Sách VIP
          </div>
        </div>
        <div className="flex items-center gap-8">
          <div className="flex items-center">
            <Input
              ref={inputSearchRef}
              inputClassName="!h-8 !pl-4 !pr-16 !bg-white text-sm !rounded-[20px] font-[SVN-Gilroy] font-medium tracking-[0.028px]"
              className={clsx("w-0 hidden", {
                "!w-[296px] max-xl:!w-[220px] max-lg:!w-[120px] !block":
                  isSearchInputShow,
              })}
              value={keyword}
              onChange={onInputChange}
              onKeyDown={onKeyDown}
              inputIcon={Images.SearchNormalIcon.default}
              onIconClick={onIconSearchClick}
              inputIconClassName="!right-4 !top-1 !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
              inputElement={
                <img
                  src={Images.CloseCircleIcon.default}
                  alt=""
                  onClick={handleClearClick}
                />
              }
              inputElementClassName="!right-11 !top-2 !left-auto !w-4 !h-4 cursor-pointer hover:bg-gray-300"
            />
            <img
              className={clsx("cursor-pointer block transition-all", {
                "!hidden": isSearchInputShow,
              })}
              src={Images.SearchNormalIcon.default}
              alt=""
              onClick={handleSearchInputShow}
            />
          </div>
          {!!userInfo.id && (
            <div className="flex items-center">
              <img
                className="w-[16px] h-[16px]"
                src={Images.CrownIcon.default}
                alt=""
              />
              <p
                onClick={handleNavigateUpgrade}
                className="text-transparent bg-gradient-to-r from-[#FC8B22] to-[#ffd919] bg-clip-text font-bold ml-1 cursor-pointer"
              >
                Nâng cấp
              </p>
            </div>
          )}
          <div className="flex gap-3 items-center">
            {!userInfo.id ? (
              <>
                <Button
                  width="auto"
                  label="Đăng ký"
                  className="!w-[95px] !h-11 font-bold !bg-transparent !text-[#38499b] border border-[#38499b] !rounded-[22px]"
                  onClick={handleOpenSignUpModal}
                />
                <Button
                  width="auto"
                  className="!w-[116px] !h-11 font-bold bg-gradient-to-b from-[#8092e4] to-[#38499b] !rounded-[22px]"
                  label="Đăng nhập"
                  onClick={handleOpenSignInModal}
                />
              </>
            ) : (
              <div className="flex gap-8 items-center">
                <div className="relative select-none">
                  <img
                    className="cursor-pointer"
                    src={Images.Notification.default}
                    alt=""
                    onClick={handleNotificationClick}
                  />
                  <div
                    className={clsx(
                      "absolute -top-1 -right-1 w-[10px] h-[10px] bg-[#FC4040] rounded-full hidden",
                      {
                        "!block": notifications.some((item) => !item.isRead),
                      }
                    )}
                  ></div>
                  {!!isNotificationOpen && (
                    <div
                      className={clsx(
                        "absolute right-0 top-8 w-[418px] max-h-[696px] overflow-y-auto bg-white border border-[#DFE2E7] rounded-lg",
                        "pb-5 px-4 shadow-[0_8px_20px_0_rgba(255,255,255,0.28)] z-10 overscroll-contain"
                      )}
                    >
                      <div className="flex justify-between items-center sticky top-0 bg-white pt-5 z-10">
                        <div className="font-bold font-[SVN-Gilroy] text-xl text-[#171A20]">
                          Thông báo
                        </div>
                        <div
                          className="font-medium font-[SVN-Gilroy] text-sm text-[#38499b] cursor-pointer"
                          onClick={handleMarkAllReadClick}
                        >
                          Đánh dấu đã đọc tất cả
                        </div>
                      </div>
                      <div className="mt-4 flex flex-col gap-3">
                        {!!notifications.length &&
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className="p-3 cursor-pointer hover:bg-[#DFE2E7] rounded-lg relative"
                              onClick={() =>
                                handleNotificationItemClick(notification)
                              }
                            >
                              <div className="flex gap-3">
                                <div className="w-[52px] h-[52px] rounded-full">
                                  <img
                                    className="w-[52px] rounded-full"
                                    src={notification.thumbnail}
                                    alt=""
                                  />
                                </div>
                                <div>
                                  <div className="font-[SVN-Gilroy] text-[#171A20] font-semibold">
                                    {notification.content}
                                  </div>
                                  <div className="font-[SVN-Gilroy] text-sm text-[#616E87] font-medium">
                                    {formatDateTime(notification.createdDate)}
                                  </div>
                                </div>
                              </div>
                              {!notification.isRead && (
                                <div className="mt-2 font-[SVN-Gilroy] font-medium text-sm text-[#272C36] tracking-[0.028px]">
                                  Đọc ngay tại đây!
                                </div>
                              )}
                              <div
                                className={clsx(
                                  "absolute top-1 right-1 w-[10px] h-[10px] bg-[#FC4040] rounded-full hidden",
                                  {
                                    "!block": !notification.isRead,
                                  }
                                )}
                              ></div>
                            </div>
                          ))}
                        {requestParams?.pageNum &&
                          meta &&
                          requestParams.pageNum < meta.totalPage && (
                            <Button
                              containerClassName="mx-auto"
                              label="Xem thêm"
                              labelClassName="font-[SVN-Gilroy]"
                              width="fit-content"
                              className="text-sm font-semibold px-11"
                              onClick={onLoadMoreClick}
                            />
                          )}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  ref={userMenuRef}
                  className="flex cursor-pointer"
                  onClick={handleUserMenuClick}
                >
                  <div className="relative w-10 h-10 rounded-full">
                    <img
                      className="w-10 h-10 rounded-full object-cover"
                      src={userInfo.thumbnail}
                      alt=""
                    />
                    {!!isUserMenuOpen && (
                      <div className="absolute w-36 max-lg:w-28 p-3 max-lg:p-2.5 bg-white border border-[#DFE2E7] rounded-lg shadow-[0_8px_20px_0_rgba(255,255,255,0.28)] z-10">
                        <div
                          className="text-center hover:bg-gray-100 hover:rounded-lg py-2 px-1 max-lg:text-sm"
                          onClick={handleProfileClick}
                        >
                          Tài khoản
                        </div>
                        <div
                          className="text-center hover:bg-gray-100 hover:rounded-lg py-2 px-1 max-lg:text-sm"
                          onClick={handleLogoutClick}
                        >
                          Đăng xuất
                        </div>
                      </div>
                    )}
                  </div>
                  <img src={Images.AltArrowDownIcon.default} alt="" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Outlet />
      <Footer />
      <div
        className={clsx(
          "fixed inset-0 bg-black bg-opacity-50 justify-center items-center z-[1000000] hidden",
          {
            "!flex": isSignInModalOpen && !userInfo.id,
          }
        )}
      >
        <div ref={signInModalRef} className="bg-white p-6 rounded-lg shadow-lg">
          <SignIn
            onSwitchToSignUp={handleOpenSignUpModal}
            onSwitchToForgotPassword={handleOpenForgotPasswordModal}
            onSubmit={onSignInSubmit}
            onGoogleLoginSuccess={onGoogleLoginSuccess}
            onFacebookLoginSuccess={onFacebookLoginSuccess}
          />
        </div>
      </div>
      <div
        className={clsx(
          "fixed inset-0 bg-black bg-opacity-50 justify-center items-center z-[1000000] hidden",
          {
            "!flex": isSignUpModalOpen && !userInfo.id,
          }
        )}
      >
        <div ref={signUpModalRef} className="bg-white rounded-lg shadow-lg">
          <SignUp
            onSwitchToSignIn={handleOpenSignInModal}
            onSubmit={onSignUpSubmit}
            onGoogleLoginSuccess={onGoogleLoginSuccess}
            onFacebookLoginSuccess={onFacebookLoginSuccess}
          />
        </div>
      </div>
    </div>
  );
};

export default UserLayout;
